import { useState } from 'react';
import { Button, Input, Modal } from '@react-apps/design-system';
import type { Product } from '../../domain/entities/catalog.entity';
import { useCreateProduct } from '../hooks/use-products';
import {
  EMPTY_PRODUCT_EXTERNAL_KEY,
  isProductExternalKeyComplete,
  ProductExternalKeyFields,
  trimProductExternalKey,
  type ProductExternalKeyFormValue,
} from './ProductExternalKeyFields';

interface CreateProductModalProps {
  open: boolean;
  onClose: () => void;
  /** Chamado com o produto recém-criado, antes de fechar. */
  onCreated?: (product: Product) => void;
  /** Sobrescreve a descrição padrão do modal (ex.: criar a partir de outro). */
  modalDescription?: string;
}

export function CreateProductModal({
  open,
  onClose,
  onCreated,
  modalDescription,
}: CreateProductModalProps) {
  const [name, setName] = useState('');
  const [externalKey, setExternalKey] = useState<ProductExternalKeyFormValue>(
    EMPTY_PRODUCT_EXTERNAL_KEY,
  );
  const { mutate, isPending, error, reset } = useCreateProduct();

  const close = () => {
    reset();
    setName('');
    setExternalKey(EMPTY_PRODUCT_EXTERNAL_KEY);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate(
      {
        name: name.trim(),
        ...trimProductExternalKey(externalKey),
      },
      {
        onSuccess: (product) => {
          if (onCreated) {
            onCreated(product);
          } else {
            close();
          }
        },
      },
    );
  };

  const canSubmit =
    name.trim().length > 0 &&
    isProductExternalKeyComplete(externalKey) &&
    !isPending;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Criar produto"
      description={
        modalDescription ??
        'O produto nasce com status “Sem configuração”. Associe perks e ajuste o status na tela de detalhe.'
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nome"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <ProductExternalKeyFields
          value={externalKey}
          onChange={setExternalKey}
        />
        <div className="border-(--color-border) bg-(--color-bg-secondary) space-y-1.5 rounded-lg border p-3 text-xs">
          <p className="text-(--color-text-primary) font-medium">
            Por que produto e oferta?
          </p>
          <p className="text-(--color-text-secondary)">
            O que o aluno ganha é decidido pelo{' '}
            <span className="italic">par</span> produto × oferta: a oferta A do
            produto X pode dar 6 meses e a oferta B, os mesmos 6 meses mais um
            bônus. Cadastre um produto aqui para cada oferta que entrega coisas
            diferentes.
          </p>
          <p className="text-(--color-text-secondary)">
            Uma compra cuja oferta não estiver cadastrada{' '}
            <span className="italic">não é entregue</span> — não há fallback
            para outra oferta do mesmo produto.
          </p>
        </div>
        {error && (
          <p className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Falha ao criar produto'}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isPending ? 'Criando…' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
